// PR-F2 — staff card-surcharge configuration grid at
// /portfolio-admin/card-surcharge. Per-US-state policy for buyer card
// payments: whether a card is allowed, and the max surcharge rate that
// applies. The rate is a FRACTION on the wire (0.03) but edited here as a
// PERCENT (×100 on display, ÷100 on save — the broker-fee percent
// convention). Each row saves independently. Gated Portfolio.ManageTpsStripe
// (the route already requires it; this guard is defense-in-depth).

import { useEffect, useState } from "react";
import {
  EmptyState,
  Input,
  PageHeader,
  Skeleton,
  SubmitButton,
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  useToast,
} from "@/design-system/components/ui";
import { extractErrorMessage } from "@/api/client";
import { useAuth } from "@/hooks/useAuth";
import type { CardSurchargeConfigDto } from "@/api/portfolioAdmin/cardSurchargeApi";
import {
  useCardSurchargeConfigs,
  useUpdateCardSurcharge,
} from "@/hooks/portfolioAdmin/useCardSurcharge";

const MANAGE_PERMISSION = "Portfolio.ManageTpsStripe";

export default function CardSurchargeConfigPage() {
  const { hasPermission } = useAuth();
  const query = useCardSurchargeConfigs();

  if (!hasPermission(MANAGE_PERMISSION)) {
    return (
      <div className="p-space-5">
        <PageHeader eyebrow="Finance" title="Card surcharge" />
        <EmptyState
          title="Not authorized"
          description="You don't have permission to manage card-surcharge settings."
        />
      </div>
    );
  }

  return (
    <div className="p-space-5">
      <PageHeader
        eyebrow="Finance"
        title="Card surcharge"
        description="Per-state policy for buyer card payments on TPS statements: whether cards are accepted and the maximum surcharge rate. Credit-card surcharges are sized from the buyer's actual card at payment time, never above this ceiling; debit is never surcharged."
      />

      {query.isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : query.isError ? (
        <p
          className="text-small text-status-error"
          role="alert"
          data-testid="card-surcharge-error"
        >
          Couldn't load card-surcharge settings. Try again.
        </p>
      ) : !query.data || query.data.length === 0 ? (
        <EmptyState
          title="No states configured"
          description="No card-surcharge rows are seeded yet."
        />
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>State</TH>
              <TH>Cards allowed</TH>
              <TH className="text-right">Max surcharge</TH>
              <TH className="text-right">Save</TH>
            </TR>
          </THead>
          <TBody>
            {query.data.map((config) => (
              <SurchargeRow key={config.stateCode} config={config} />
            ))}
          </TBody>
        </Table>
      )}
    </div>
  );
}

function SurchargeRow({ config }: { config: CardSurchargeConfigDto }) {
  const toast = useToast();
  const update = useUpdateCardSurcharge();

  const [cardAllowed, setCardAllowed] = useState(config.cardAllowed);
  // Edited as a percent string; the server stores a fraction.
  const [percent, setPercent] = useState(() => (config.surchargeRate * 100).toString());

  // Re-seed the draft if the server row changes (e.g. after another row's
  // save invalidates and refetches the list).
  useEffect(() => {
    setCardAllowed(config.cardAllowed);
    setPercent((config.surchargeRate * 100).toString());
  }, [config.cardAllowed, config.surchargeRate]);

  const parsedPercent = Number(percent);
  const percentInvalid = percent.trim() === "" || Number.isNaN(parsedPercent);
  // Server bound is 0.04 (= 4%); mirror it client-side for a clear message.
  const percentOutOfRange = !percentInvalid && (parsedPercent < 0 || parsedPercent > 4);

  const save = () => {
    if (percentInvalid || percentOutOfRange) return;
    update.mutate(
      {
        stateCode: config.stateCode,
        body: { cardAllowed, surchargeRate: parsedPercent / 100 },
      },
      {
        onSuccess: () => toast.success(`${config.stateCode} saved`),
        onError: (err) => toast.error(extractErrorMessage(err)),
      },
    );
  };

  return (
    <TR data-testid={`card-surcharge-row-${config.stateCode}`}>
      <TD className="font-semibold text-onyx">{config.stateCode}</TD>
      <TD>
        <label className="inline-flex items-center gap-space-2 text-small text-onyx">
          <input
            type="checkbox"
            className="h-4 w-4 accent-gold"
            checked={cardAllowed}
            onChange={(e) => setCardAllowed(e.target.checked)}
            data-testid={`card-surcharge-allowed-${config.stateCode}`}
          />
          <span className="text-gray-mid">{cardAllowed ? "Allowed" : "Not allowed"}</span>
        </label>
      </TD>
      <TD className="text-right">
        <div className="inline-flex items-center justify-end gap-space-2">
          <Input
            type="number"
            step="0.01"
            min={0}
            max={4}
            value={percent}
            onChange={(e) => setPercent(e.target.value)}
            invalid={percentInvalid || percentOutOfRange}
            className="w-24 text-right tabular-nums"
            aria-label={`${config.stateCode} max surcharge percent`}
            data-testid={`card-surcharge-rate-${config.stateCode}`}
          />
          <span className="text-gray-mid">%</span>
        </div>
        {percentOutOfRange && (
          <p className="mt-space-1 text-caption text-status-error" role="alert">
            Must be between 0% and 4%.
          </p>
        )}
      </TD>
      <TD className="text-right">
        <SubmitButton
          type="button"
          variant="primary"
          size="sm"
          pending={update.isPending}
          pendingLabel="Saving…"
          disabled={percentInvalid || percentOutOfRange}
          onClick={save}
          data-testid={`card-surcharge-save-${config.stateCode}`}
        >
          Save
        </SubmitButton>
      </TD>
    </TR>
  );
}
